/**
 * Ui Clipping (#366) — the composer placeholder used to be a single fixed
 * string ("Type your message... (Shift+Enter for new line)"), sized only by
 * its own text. At the chat's narrowest supported split width the textarea's
 * real writable width (after its own left/right padding — including the
 * ~112px `pr-28` reserved for the mic/send overlay) shrinks well below what
 * that sentence needs, so the browser wraps it onto several lines and the
 * box's fixed one-line height + `overflow-y-hidden` clips everything past
 * the first — the reported "Type your".
 *
 * `pickPlaceholder` is the pure selection rule: given candidate strings
 * ordered LONGEST (most descriptive) → SHORTEST, and a `measure(text)` pixel-
 * width function bound to the composer's OWN live font, it returns the
 * FIRST (longest) candidate that fits `availableWidth`. The caller
 * (ChatInput) re-measures on every width change via a ResizeObserver, so the
 * placeholder degrades and recovers automatically as a feature panel opens/
 * closes or the split divider is dragged — never a guessed character-count
 * threshold.
 *
 * FAILS OPEN, deliberately: a candidate that cannot be measured at all
 * (`measure` returns a non-finite width — no working text measurer, e.g. no
 * canvas 2d context) is treated as FITTING rather than as not fitting. This
 * matters because measurement genuinely can be unavailable (a test
 * environment without canvas support; an exotic browser) and the product
 * default — the full, most descriptive placeholder — is a far better
 * fallback than silently shrinking the composer's copy for no visible
 * reason. Only a candidate that WAS measured and genuinely does not fit is
 * skipped; if every measured candidate is too wide, the shortest one is
 * returned so the placeholder never wraps/clips.
 */
export function pickPlaceholder(candidates, availableWidth, measure) {
    if (!Array.isArray(candidates) || candidates.length === 0) return '';
    const fullest = candidates[0];
    const shortest = candidates[candidates.length - 1];
    if (typeof measure !== 'function') return fullest;
    if (!Number.isFinite(availableWidth) || availableWidth <= 0) return fullest;
    for (const candidate of candidates) {
        const width = measure(candidate);
        if (!Number.isFinite(width) || width <= availableWidth) return candidate;
    }
    return shortest;
}
